/**
 * PublicRoute - Public access guard with authenticated redirect
 *
 * Allows unauthenticated access to public routes (login, register, etc.)
 * Redirects authenticated users to their appropriate dashboard based on role.
 */

import { type ReactNode, useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AuthContext } from "@/contexts/AuthContext";
import { LoadingScreen } from "@/app/fallbacks/LoadingScreen";
import { ROUTES } from "@/app/routes";
import { getPostLoginDestination } from "@/app/getPostLoginDestination";
import { getDefaultRoute } from "@/app/getDefaultRoute";

interface PublicRouteProps {
  children: ReactNode;
}

export const PublicRoute = ({ children }: PublicRouteProps) => {
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error("PublicRoute must be used within an AuthProvider");
  }

  const { isAuthenticated, isLoading } = authContext;
  const location = useLocation();

  // Only show loading on initial mount, not during login attempts
  if (isLoading && !location.pathname.includes(ROUTES.AUTH.LOGIN)) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    const defaultRoute = getDefaultRoute({
      isPlatformAdmin: authContext.tokenManagement.isPlatformAdmin(),
      isInvestorOnly: authContext.tokenManagement.isInvestorOnly(),
    });

    // Use intended destination if available, otherwise use role-based default
    const from = getPostLoginDestination(location.state, defaultRoute);
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
};
